import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    FormControl,
    TextField,
    Alert,
    Grid2,
    Select,
    MenuItem,
    InputLabel,
    FormControlLabel,
    Switch,
    CircularProgress,
} from '@mui/material';
import { AutoCompleteComponent } from '@syncfusion/ej2-react-dropdowns';
import { useAppSnackbar } from '../../contexts/AppSnackbarContext';

import { addBusinessDays } from '../../utils/dateUtils';

const TASK_STATES = {
    Pending: 0,
    InProgress: 1,
    // Completed: 2, //not relevant for creation
    // Cancelled: 3,
}

const TASK_PRIORITIES = {
    Low: 0,
    Medium: 1,
    High: 2,
    Urgent: 3,
}

export default function CreateTask({ onClose }) {
    const [validationError, setValidationError] = useState({});
    const [showError, setShowError] = useState(false);
    const [hasDueDate, setHasDueDate] = useState(false);
    const [attentionDays, setAttentionDays] = useState(1);
    const [requestError, setRequestError] = useState(null);
    const [createLoading, setCreateLoading] = useState(false);
    const [searchedUsers, setSearchedUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [createForm, setCreateForm] = useState({
        name: '',
        description: '',
        state: TASK_STATES.Pending,
        dueDate: null,
        assignedUser: null,
        priority: TASK_PRIORITIES.Low,
    });

    const { showSnackbar } = useAppSnackbar();

    const searchUsers = async () => {
        try {
            const res = await fetch(`/api/security/users-search?query=${searchQuery}`, {
                method: 'GET',
                credentials: 'include',
            });
            if (!res.ok) {
                throw new Error(`Error: ${res.status} ${res.statusText}`);
            }
            const data = await res.json();
            setSearchedUsers(data);
        } catch (error) {
            console.error('Error fetching users:', error);
            setRequestError(error.message);
            setSearchedUsers([]);
        }
    }

    const postCreateTask = async () => {
        setCreateLoading(true);
        try {
            const res = await fetch('/api/tasks', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...createForm,
                    dueDate: hasDueDate ? addBusinessDays(new Date(), attentionDays) : null,
                }),
            });
            if (!res.ok) {
                throw new Error(`Error: ${res.status} ${res.statusText}`);
            }
            onClose();
            setCreateForm({
                name: '',
                description: '',
                state: TASK_STATES.Pending,
                dueDate: null,
                assignedUser: null,
                priority: TASK_PRIORITIES.Low,
            });
            showSnackbar("Task created", "success");
        } catch (error) {
            console.error('Error creating task:', error);
            setRequestError(error.message);
        } finally {
            setCreateLoading(false);
        }
    }

    useEffect(() => {
        const id = setTimeout(() => {
            if (searchQuery && searchQuery.trim() !== "" && searchQuery.length >= 3) {
                searchUsers();
            } else {
                setSearchedUsers([]);
            }
        }, 300);
        return () => clearTimeout(id);
    }, [searchQuery]);

    const handleInputChange = (key, value, required) => {
        if (required) {
            if (value.trim() === "") {
                setValidationError((prev) => ({
                    ...prev,
                    [key]: `${key.charAt(0).toUpperCase() + key.slice(1)} is required`
                }));
            } else {
                setValidationError((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[key];
                    if (Object.keys(newErrors).length === 0) {
                        setShowError(false);
                    }
                    return newErrors;
                });
            }
        }
        setCreateForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    }

    const handleDueDateCheckChange = (value) => setHasDueDate(value);

    const handleAttentionDaysChange = (value) => setAttentionDays(value);

    const handleCreateSubmit = async (e) => {
        e.preventDefault();

        if (Object.keys(validationError).length > 0) {
            setShowError(true);
            return;
        }

        if (!createForm.name || createForm.name.trim() === "") {
            setValidationError((prev) => ({
                ...prev,
                name: 'Name is required'
            }));
            setShowError(true);
            return;
        }

        if (hasDueDate && (attentionDays == null || attentionDays < 1)) {
            setValidationError((prev) => ({
                ...prev,
                attentionDays: 'Attention days must be at least 1'
            }));
            setShowError(true);
            return;
        } else {
            setValidationError((prev) => {
                const newErrors = { ...prev };
                delete newErrors['attentionDays'];
                return newErrors;
            });
            setShowError(false);
        }

        if (searchQuery && searchQuery.trim() !== "" && !createForm.assignedUser) {
            setValidationError((prev) => ({
                ...prev,
                assignedUser: 'Please select a valid user from the list'
            }));
            setShowError(true);
            return;
        } else {
            setValidationError((prev) => {
                const newErrors = { ...prev };
                delete newErrors['assignedUser'];
                return newErrors;
            });
            setShowError(false);
        }

        await postCreateTask();
    }

    const handleSearchUsers = (e) => {
        setSearchQuery(e.target.value);
        setCreateForm((prev) => ({
            ...prev,
            assignedUser: searchedUsers.find(user => user.email === e.target.value)?.id || null,
        }));
        setValidationError((prev) => {
            const newErrors = { ...prev };
            delete newErrors['assignedUser'];
            return newErrors;
        });
        setShowError(false);
    }
    return (
        <Box>
            {showError && validationError && Object.keys(validationError).length > 0 && (
                <Alert severity="error" style={{ marginBottom: 20 }}>
                    {Object.values(validationError).map((error, index) => (
                        <div key={index}>{error}</div>
                    ))}
                </Alert>
            )}
            <form onSubmit={handleCreateSubmit}>
                <Grid2 container columns={16}>
                    <Grid2 size={8} sx={{ px: 2 }}>
                        <FormControl fullWidth margin="normal">
                            <TextField
                                error={validationError['name'] != null}
                                helperText={validationError['name']}
                                id='task-name'
                                label="Name"
                                onChange={(e) => handleInputChange('name', e.target.value, true)}
                                variant="outlined"
                                name='task-name'
                                value={createForm.name ?? ""}
                            />
                        </FormControl>
                    </Grid2>
                    <Grid2 size={8} sx={{ px: 2 }}>
                        <FormControl fullWidth margin="normal">
                            <InputLabel id='task-state'>State</InputLabel>
                            <Select
                                error={validationError['state'] != null}
                                labelId='task-state'
                                id='task-state'
                                value={createForm.state}
                                label="State"
                                onChange={(e) => handleInputChange('state', e.target.value)}
                                variant="outlined"
                            >
                                {Object.keys(TASK_STATES).map(key => (
                                    <MenuItem key={key} value={TASK_STATES[key]}>
                                        {key}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid2>
                </Grid2>
                <Grid2 container columns={16}>
                    <Grid2 size={8} sx={{ px: 2 }}>
                        <FormControl fullWidth margin="normal">
                            <TextField
                                id='task-description'
                                label="Description"
                                onChange={(e) => handleInputChange('description', e.target.value, false)}
                                variant="outlined"
                                name='task-description'
                                value={createForm.description ?? ""}
                                multiline
                                minRows={4}
                                maxRows={4}
                            />
                        </FormControl>
                    </Grid2>
                    <Grid2 size={8} sx={{ px: 2 }}>
                        <FormControl fullWidth margin="normal">
                            <InputLabel id='task-priority'>Priority</InputLabel>
                            <Select
                                error={validationError['priority'] != null}
                                labelId='task-priority'
                                id='task-priority'
                                value={createForm.priority}
                                label="Priority"
                                onChange={(e) => handleInputChange('priority', e.target.value)}
                                variant="outlined"
                            >
                                {Object.keys(TASK_PRIORITIES).map(key => (
                                    <MenuItem key={key} value={TASK_PRIORITIES[key]}>
                                        {key}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid2>
                </Grid2>
                <Grid2 container columns={16}>
                    <Grid2 size={8} sx={{ px: 2 }}>
                        <FormControlLabel control={<Switch onChange={(e) => handleDueDateCheckChange(e.target.checked)} />} label="Due Date" />
                        <FormControl fullWidth margin='normal'>
                            <TextField
                                error={validationError['attentionDays'] != null}
                                helperText={validationError['attentionDays']}
                                id='task-attentionDays'
                                label="Attention days"
                                onChange={(e) => handleAttentionDaysChange(e.target.value)}
                                variant="outlined"
                                name='task-attentionDays'
                                value={attentionDays ?? 0}
                                type='number'
                                inputProps={{ min: 1 }}
                                disabled={!hasDueDate}
                            />
                        </FormControl>
                    </Grid2>
                    <Grid2 size={8} sx={{ px: 2 }}>
                        {/* Placeholder for assigned user selection */}
                        <AutoCompleteComponent
                            id="search-users"
                            dataSource={searchedUsers}
                            fields={{
                                value: 'email'
                            }}
                            value={searchQuery}
                            debounceDelay={300}
                            popupHeight="200px"
                            onChange={handleSearchUsers}
                            placeholder="john@email.com" />
                    </Grid2>
                </Grid2>

                <Button type="submit" variant="contained" color="primary" sx={{ m: 2 }} disabled={createLoading || (showError && validationError && Object.keys(validationError).length > 0)}>
                    {createLoading ? <CircularProgress /> : 'Create Task'}
                </Button>
                {requestError && (
                    <Alert severity="error" style={{ my: 2 }}>
                        {requestError}
                    </Alert>
                )}
            </form>
        </Box>
    )
}
